import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Stack,
} from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { resolveEventTheme, eventSurfaceSx } from '../eventThemes';
import { CalendarToday, LocationOn } from '@mui/icons-material';
import axios from 'axios';
import { sanitizeHtml } from '../utils/sanitize';
import EventNavigation from './EventNavigation';

interface RSVP {
  id: number;
  name: string;
  attending: string;
  bringing_guests: string;
  guest_count: number;
  guest_names: string[] | string;
  items_bringing: string[] | string;
  other_items?: string;
}

interface Event {
  id: number;
  title: string;
  description: string;
  date: string;
  location: string;
  slug: string;
  needed_items?: string[] | string;
  wallpaper?: string;
  wallpaper_position?: string;
  wallpaper_zoom?: number;
  rsvp_cutoff_date?: string;
  theme_color?: string;
  theme_id?: string | null;
  font_style?: string;
}

const parseOtherItems = (value?: string): string[] => {
  if (!value) return [];
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};


const EventView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const baseTheme = useTheme();
  const [event, setEvent] = useState<Event | null>(null);
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [neededItems, setNeededItems] = useState<string[]>([]);
  const [claimedItems, setClaimedItems] = useState<string[]>([]);
  const [otherItems, setOtherItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const eventTheme = useMemo(() => resolveEventTheme(baseTheme, event || {}), [event, baseTheme]);

  useEffect(() => {
    fetchEventAndRsvps();
  }, [slug]);

  const fetchEventAndRsvps = async () => {
    try {
      const [eventResponse, rsvpsResponse] = await Promise.all([
        axios.get(`/api/events/${slug}`),
        axios.get(`/api/events/${slug}/rsvps`)
      ]);
      setEvent(eventResponse.data);

      let items: string[] = [];
      if (eventResponse.data.needed_items) {
        try {
          if (typeof eventResponse.data.needed_items === 'string') {
            const parsed = JSON.parse(eventResponse.data.needed_items);
            items = Array.isArray(parsed) ? parsed : [];
          } else if (Array.isArray(eventResponse.data.needed_items)) {
            items = eventResponse.data.needed_items;
          }
        } catch (e) {
          console.error('Error parsing needed_items:', e);
          items = [];
        }
      }

      const claimed = new Set<string>();
      const otherItemsSet = new Set<string>();
      const processedRsvps = rsvpsResponse.data.map((rsvp: RSVP) => {
        let itemsBringing: string[] = [];
        try {
          if (typeof rsvp.items_bringing === 'string') {
            try {
              const parsed = JSON.parse(rsvp.items_bringing);
              itemsBringing = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              console.error('Error parsing items_bringing JSON:', e);
            }
          } else if (Array.isArray(rsvp.items_bringing)) {
            itemsBringing = rsvp.items_bringing;
          }
          itemsBringing.forEach(item => claimed.add(item));
        } catch (e) {
          console.error('Error processing items for RSVP:', e);
        }
        parseOtherItems(rsvp.other_items).forEach((item) => otherItemsSet.add(item));
        return { ...rsvp, items_bringing: itemsBringing };
      });

      setRsvps(processedRsvps);
      setClaimedItems(Array.from(claimed).sort((a, b) => a.localeCompare(b)));
      setNeededItems(items.filter(item => !claimed.has(item)).sort((a, b) => a.localeCompare(b)));
      setOtherItems(Array.from(otherItemsSet).sort((a, b) => a.localeCompare(b)));
      setLoading(false);
    } catch (error) {
      setError('Failed to load event data');
      setLoading(false);
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error || !event) {
    return <Typography color="error">{error || 'Event not found'}</Typography>;
  }

  const totalGuests = rsvps.reduce((total, rsvp) => {
    const rsvpCount = rsvp.attending === 'yes' ? 1 : 0;
    const guestCount = (rsvp.attending === 'yes' && rsvp.bringing_guests === 'yes') ? rsvp.guest_count : 0;
    return total + rsvpCount + guestCount;
  }, 0);

  const content = (
    <Box sx={eventSurfaceSx(event)}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
          {event.title}
        </Typography>
        <EventNavigation slug={slug || ''} page="view" rsvpClosed={Boolean(event.rsvp_cutoff_date && new Date() > new Date(event.rsvp_cutoff_date))} />
      </Box>

      {/* Event info card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {event.wallpaper && (() => {
          const pos = (event.wallpaper_position || '50% 50%').split(/\s+/);
          const px = Number.isFinite(parseFloat(pos[0])) ? parseFloat(pos[0]) : 50;
          const py = Number.isFinite(parseFloat(pos[1])) ? parseFloat(pos[1]) : 50;
          const z = event.wallpaper_zoom ?? 1;
          return (
            <Box sx={{ width: '100%', aspectRatio: '16 / 5', overflow: 'hidden', position: 'relative', borderRadius: 2, mb: 2 }}>
              <Box component="img" src={event.wallpaper} alt={event.title} sx={{
                position: 'absolute', left: `${px}%`, top: `${py}%`,
                transform: `translate(-${px}%, -${py}%)`,
                width: `${100 * z}%`, height: 'auto', display: 'block',
              }} />
            </Box>
          );
        })()}
        {event.description && (
          <Box
            sx={{
              mb: 2,
              color: 'text.secondary',
              fontSize: '0.875rem',
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
              '& *': { maxWidth: '100%' },
              '& p': { margin: 0, lineHeight: 1.6, whiteSpace: 'normal' },
              '& p + p': { marginTop: '0.75em' },
              '& p:empty': { marginTop: '0.5em' },
              '& br': { display: 'block', content: '""', marginTop: '0.5em' },
              '& ul, & ol': { marginLeft: 2, marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '1.5em' },
              '& li': { marginBottom: 0, lineHeight: 1.6 },
              '& h1, & h2, & h3': { marginTop: '0.75em', marginBottom: '0.5em' },
              '& a': { color: 'primary.main' },
              '& img': { maxWidth: '100%', height: 'auto' },
              '& pre, & code': { whiteSpace: 'pre-wrap', maxWidth: '100%', overflow: 'auto' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }}
          />
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip icon={<CalendarToday />} label={new Date(event.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })} variant="outlined" size="small" />
          <Chip icon={<LocationOn />} label={event.location} variant="outlined" size="small" />
          {event.rsvp_cutoff_date && (
            <Chip label={`Cutoff: ${new Date(event.rsvp_cutoff_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`} variant="outlined" size="small" />
          )}
        </Stack>
      </Paper>

      {/* Items status card */}
      <Paper sx={{ p: 3, mb: 3, overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Items Status</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Still Needed</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {neededItems.length > 0 ? neededItems.map((item, index) => (
                <Chip key={`${item}-${index}`} label={item} color="primary" variant="outlined" size="small" />
              )) : (
                <Typography variant="body2" color="text.secondary">All items claimed</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Claimed</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {claimedItems.length > 0 ? claimedItems.map((item, index) => (
                <Chip key={`${item}-${index}`} label={item} color="success" size="small" />
              )) : (
                <Typography variant="body2" color="text.secondary">No items claimed yet</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Other Items</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {otherItems.length > 0 ? otherItems.map((item, index) => (
                <Chip key={`${item}-${index}`} label={item} color="warning" size="small" />
              )) : (
                <Typography variant="body2" color="text.secondary">None</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* RSVPs table card */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          RSVPs ({rsvps.length}) &middot; Total Guests: {totalGuests}
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Attending</TableCell>
                <TableCell>Guests</TableCell>
                <TableCell>Needed Items</TableCell>
                <TableCell>Other Items</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rsvps.map((rsvp) => (
                <TableRow key={rsvp.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{rsvp.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={rsvp.attending.charAt(0).toUpperCase() + rsvp.attending.slice(1)}
                      color={rsvp.attending === 'yes' ? 'success' : 'default'}
                      size="small"
                      variant={rsvp.attending === 'yes' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {rsvp.bringing_guests === 'yes'
                      ? `${rsvp.guest_count} (${Array.isArray(rsvp.guest_names)
                          ? rsvp.guest_names.map(name => name.trim()).join(', ')
                          : typeof rsvp.guest_names === 'string'
                            ? rsvp.guest_names.replace(/\s+/g, ', ').trim()
                            : 'No names provided'})`
                      : 'No'
                    }
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(() => {
                        let items: string[] = [];
                        try {
                          if (typeof rsvp.items_bringing === 'string') {
                            try {
                              const parsed = JSON.parse(rsvp.items_bringing);
                              items = Array.isArray(parsed) ? parsed : [];
                            } catch (e) { /* ignore */ }
                          } else if (Array.isArray(rsvp.items_bringing)) {
                            items = rsvp.items_bringing;
                          }
                        } catch (e) { /* ignore */ }

                        return items.length > 0 ? items.map((item, index) => (
                          <Chip
                            key={`${item}-${index}`}
                            label={item}
                            color="success"
                            size="small"
                            variant={claimedItems.includes(item) ? 'filled' : 'outlined'}
                          />
                        )) : (
                          <Typography variant="body2" color="text.secondary">None</Typography>
                        );
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(() => {
                        const parsed = parseOtherItems(rsvp.other_items);
                        return parsed.length > 0 ? parsed.map((item, index) => (
                          <Chip key={`${item}-${index}`} label={item} color="warning" size="small" />
                        )) : (
                          <Typography variant="body2" color="text.secondary">None</Typography>
                        );
                      })()}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  return eventTheme ? (
    <ThemeProvider theme={eventTheme}>{content}</ThemeProvider>
  ) : content;
};

export default EventView;
